import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Collapse,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  WifiTethering as ConnectionIcon,
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import ApiService from '../services/api';
import CaseTemplatesTab from '../components/CaseTemplatesTab';
import CaseFieldOptionsTab from '../components/CaseFieldOptionsTab';
import MailboxesTab from '../components/MailboxesTab';
import WorkflowsTab from '../components/WorkflowsTab';
import ServiceTypesTab from '../components/ServiceTypesTab';
import OutageNotificationsTab from '../components/OutageNotificationsTab';
import AssetsModuleTab from '../components/AssetsModuleTab';
import OrderingSettingsTab from '../components/OrderingSettingsTab';
import SupportDeskModuleSettingsTab from '../components/SupportDeskModuleSettingsTab';
import TaskModuleSettingsTab from '../components/TaskModuleSettingsTabV2';
import GoogleReviewSettingsTab from '../components/GoogleReviewSettingsTab';
import DbBackupSettingsTab from '../components/DbBackupSettingsTab';

interface ModuleSetting {
  id: string;
  module_name: string;
  is_enabled: boolean;
  settings: Record<string, any>;
  created_at: string;
  updated_at: string;
}

interface Carrier {
  id: string;
  name: string;
  carrier_type: string;
  is_enabled: boolean;
  is_active: boolean;
  description: string;
  polling_interval: number;
  last_poll_time: string | null;
  next_poll_time: string | null;
  notify_on_created?: boolean;
  notify_on_updated?: boolean;
  notify_on_resolved?: boolean;
}

interface NotificationTemplates {
  created_title_template: string;
  created_message_template: string;
  updated_title_template: string;
  updated_message_template: string;
  resolved_title_template: string;
  resolved_message_template: string;
  case_reply_title_template?: string;
  case_reply_message_template?: string;
}

interface NotificationService {
  id: string;
  service_name: string;
  service_type: string;
  is_enabled: boolean;
  is_connected: boolean;
  last_connection_check: string | null;
  connection_error: string | null;
  description: string;
}

const SettingsPage: React.FC = () => {
  // Format time using browser timezone with AEST fallback
  const formatTime = (isoString: string) => {
    if (!isoString) return '';
    
    // Parse the ISO string - if it doesn't have timezone, assume UTC
    let date: Date;
    if (isoString.endsWith('Z') || isoString.includes('+') || (isoString.match(/[+-]\d{2}:\d{2}$/) !== null)) {
      // Has timezone info
      date = new Date(isoString);
    } else {
      // No timezone - assume UTC and append Z
      date = new Date(isoString + 'Z');
    }
    
    // Try to get browser timezone, fallback to AEST
    let timeZone: string;
    try {
      // Get browser's timezone
      const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      timeZone = browserTimeZone || 'Australia/Sydney';
    } catch {
      // Fallback to AEST if timezone detection fails
      timeZone = 'Australia/Sydney';
    }
    
    try {
      return date.toLocaleString('en-AU', { 
        timeZone: timeZone,
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      // If timezone is invalid, fallback to AEST
      return date.toLocaleString('en-AU', { 
        timeZone: 'Australia/Sydney',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    }
  };



  const [modules, setModules] = useState<ModuleSetting[]>([]);
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [notificationServices, setNotificationServices] = useState<NotificationService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);
  const [testingConnection, setTestingConnection] = useState<string | null>(null);
  const [sendingTestMessage, setSendingTestMessage] = useState<string | null>(null);
  const [expandedCarriers, setExpandedCarriers] = useState<Set<string>>(new Set());
  const [templates, setTemplates] = useState<Record<string, NotificationTemplates>>({});
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateDialogService, setTemplateDialogService] = useState<NotificationService | null>(null);
  const [templateTab, setTemplateTab] = useState(0);
  const [templateForm, setTemplateForm] = useState<{ title: string; message: string }>({ title: '', message: '' });
  const [testingTemplate, setTestingTemplate] = useState(false);
  const [settingsTabIndex, setSettingsTabIndex] = useState(0);
  const [caseModuleSubTabIndex, setCaseModuleSubTabIndex] = useState(0);
  const [communicationsSubTab, setCommunicationsSubTab] = useState<'google-reviews' | 'outage-notifications'>('google-reviews');

  // Staff Users tab state
  const [staffUsers, setStaffUsers] = useState<any[]>([]);
  const [staffUsersLoading, setStaffUsersLoading] = useState(false);
  const [staffUsersError, setStaffUsersError] = useState('');
  const [staffUsersLoaded, setStaffUsersLoaded] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [modulesData, carriersData, notificationServicesData] = await Promise.all([
        ApiService.getModuleSettings(),
        ApiService.getCarriers(),
        ApiService.getNotificationServices()
      ]);
      setModules(modulesData);
      setCarriers(carriersData);
      setNotificationServices(notificationServicesData);
      
      // Load templates for each notification service
      const templatesMap: Record<string, NotificationTemplates> = {};
      for (const service of notificationServicesData) {
        try {
          const templatesData = await ApiService.getNotificationTemplates(service.id);
          templatesMap[service.id] = templatesData;
        } catch (err) {
          console.error(`Failed to load templates for ${service.id}:`, err);
        }
      }
      setTemplates(templatesMap);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleToggleModule = async (moduleName: string, currentEnabled: boolean) => {
    try {
      setUpdating(moduleName);
      setError(null);
      
      await ApiService.toggleModule(moduleName, !currentEnabled);
      
      setModules(prev => 
        prev.map(module => 
          module.module_name === moduleName
            ? { ...module, is_enabled: !currentEnabled }
            : module
        )
      );
    } catch (err: any) {
      setError(err.response?.data?.detail || `Failed to toggle ${moduleName}`);
    } finally {
      setUpdating(null);
    }
  };

  const handleToggleCarrier = async (carrierId: string, currentEnabled: boolean) => {
    try {
      setUpdating(carrierId);
      setError(null);
      
      await ApiService.toggleCarrier(carrierId, !currentEnabled);
      
      setCarriers(prev => 
        prev.map(carrier => 
          carrier.id === carrierId
            ? { ...carrier, is_enabled: !currentEnabled }
            : carrier
        )
      );
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to toggle carrier');
    } finally {
      setUpdating(null);
    }
  };

  const handleToggleNotificationService = async (serviceId: string, currentEnabled: boolean) => {
    try {
      setUpdating(serviceId);
      setError(null);
      
      await ApiService.toggleNotificationService(serviceId, !currentEnabled);
      
      setNotificationServices(prev => 
        prev.map(service => 
          service.id === serviceId
            ? { ...service, is_enabled: !currentEnabled }
            : service
        )
      );
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to toggle notification service');
    } finally {
      setUpdating(null);
    }
  };

  const handleTestConnection = async (serviceId: string) => {
    try {
      setTestingConnection(serviceId);
      setError(null);
      
      const result = await ApiService.testNotificationServiceConnection(serviceId);
      
      // Reload notification services to get updated connection status
      const notificationServicesData = await ApiService.getNotificationServices();
      setNotificationServices(notificationServicesData);
      
      if (!result.success) {
        setError(result.connection_error || 'Connection test failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to test connection');
    } finally {
      setTestingConnection(null);
    }
  };

  const handleSendTestMessage = async (serviceId: string) => {
    try {
      setSendingTestMessage(serviceId);
      setError(null);
      
      const result = await ApiService.sendTestNotificationMessage(serviceId);
      
      if (result.success) {
        setError('✅ Test message sent successfully! Check your Pushover app.');
        setTimeout(() => setError(null), 5000);
      } else {
        setError(result.detail || 'Failed to send test message');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send test message');
    } finally {
      setSendingTestMessage(null);
    }
  };

  const handleToggleCarrierNotification = async (carrierId: string, setting: 'created' | 'updated' | 'resolved', currentValue: boolean) => {
    try {
      setUpdating(`${carrierId}-${setting}`);
      setError(null);
      
      const settings: any = {};
      settings[`notify_on_${setting}`] = !currentValue;
      
      await ApiService.updateCarrierNotificationSettings(carrierId, settings);
      
      setCarriers(prev =>
        prev.map(carrier =>
          carrier.id === carrierId
            ? { ...carrier, [`notify_on_${setting}`]: !currentValue }
            : carrier
        )
      );
    } catch (err: any) {
      setError(err.response?.data?.detail || `Failed to update ${setting} notification setting`);
    } finally {
      setUpdating(null);
    }
  };

  const handleOpenTemplateDialog = async (service: NotificationService) => {
    try {
      setTemplateDialogService(service);
      setTemplateDialogOpen(true);
      setTemplateTab(0);
      
      // Load templates (always reload to get latest)
      const templatesData = await ApiService.getNotificationTemplates(service.id);
      setTemplates(prev => ({ ...prev, [service.id]: templatesData }));
      
      // Set initial form values for 'created' (tab 0)
      const serviceTemplates = templatesData || {
        created_title_template: '',
        created_message_template: '',
        updated_title_template: '',
        updated_message_template: '',
        resolved_title_template: '',
        resolved_message_template: '',
        case_reply_title_template: '',
        case_reply_message_template: ''
      };
      
      setTemplateForm({
        title: serviceTemplates.created_title_template || '',
        message: serviceTemplates.created_message_template || ''
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load templates');
      setTemplateDialogOpen(false);
      setTemplateDialogService(null);
    }
  };

  const handleCloseTemplateDialog = () => {
    setTemplateDialogOpen(false);
    setTemplateDialogService(null);
    setTemplateForm({ title: '', message: '' });
    setTemplateTab(0);
  };

  const handleTemplateTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTemplateTab(newValue);
    if (templateDialogService) {
      const serviceTemplates = templates[templateDialogService.id] || {
        created_title_template: '',
        created_message_template: '',
        updated_title_template: '',
        updated_message_template: '',
        resolved_title_template: '',
        resolved_message_template: '',
        case_reply_title_template: '',
        case_reply_message_template: ''
      };
      
      if (newValue === 0) {
        setTemplateForm({
          title: serviceTemplates.created_title_template || '',
          message: serviceTemplates.created_message_template || ''
        });
      } else if (newValue === 1) {
        setTemplateForm({
          title: serviceTemplates.updated_title_template || '',
          message: serviceTemplates.updated_message_template || ''
        });
      } else if (newValue === 2) {
        setTemplateForm({
          title: serviceTemplates.resolved_title_template || '',
          message: serviceTemplates.resolved_message_template || ''
        });
      } else {
        setTemplateForm({
          title: serviceTemplates.case_reply_title_template || '',
          message: serviceTemplates.case_reply_message_template || ''
        });
      }
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateDialogService) return;
    
    try {
      setUpdating(templateDialogService.id);
      setError(null);
      
      const eventTypes = ['created', 'updated', 'resolved', 'case_reply'];
      const currentType = eventTypes[templateTab];
      
      const updates: Record<string, string> = {
        [`${currentType}_title_template`]: templateForm.title,
        [`${currentType}_message_template`]: templateForm.message
      };
      
      const updated = await ApiService.updateNotificationTemplates(templateDialogService.id, updates);
      
      setTemplates(prev => ({
        ...prev,
        [templateDialogService.id]: {
          ...prev[templateDialogService.id],
          ...updated
        }
      }));
      
      setError('✅ Templates saved successfully!');
      setTimeout(() => setError(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save templates');
    } finally {
      setUpdating(null);
    }
  };

  const handleTestTemplate = async () => {
    if (!templateDialogService) return;
    
    try {
      setTestingTemplate(true);
      setError(null);
      
      const eventTypes = ['created', 'updated', 'resolved', 'case_reply'];
      const currentType = eventTypes[templateTab];
      
      const result = await ApiService.testNotificationTemplate(templateDialogService.id, {
        event_type: currentType,
        title_template: templateForm.title,
        message_template: templateForm.message
      });
      
      if (result.success) {
        setError(`✅ Test notification sent! Check your device.`);
        setTimeout(() => setError(null), 5000);
      } else {
        setError(result.detail || 'Failed to send test notification');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to test template');
    } finally {
      setTestingTemplate(false);
    }
  };

  const toggleCarrierExpanded = (carrierId: string) => {
    setExpandedCarriers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(carrierId)) {
        newSet.delete(carrierId);
      } else {
        newSet.add(carrierId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const masterSwitch = modules.find(m => m.module_name === 'carrier_management');

  // Same size and weight as the tabs on the Accounts page (the theme's default Tab style)
  const tabSx = {
    '&.Mui-selected': { color: 'primary.main' },
  };
  const subTabSx = {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    minHeight: 40,
    py: 0,
    '&.Mui-selected': { color: 'secondary.main', fontWeight: 600 },
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" mb={2}>
        <SettingsIcon sx={{ mr: 2, color: 'primary.main' }} />
        <Typography variant="h4" component="h1">
          Settings
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={settingsTabIndex}
          onChange={(_, v) => setSettingsTabIndex(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 48,
            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', backgroundColor: 'primary.main' },
            '& .MuiTabs-flexContainer': { gap: 0 },
          }}
        >
          <Tab label="System Settings" sx={tabSx} />
          <Tab label="Case Module" sx={tabSx} />
          <Tab label="Support Desk Module" sx={tabSx} />
          <Tab label="Tasks" sx={tabSx} />
          <Tab label="Service Types" sx={tabSx} />
          <Tab label="Assets Module" sx={tabSx} />
          <Tab label="Ordering" sx={tabSx} />
          <Tab label="Staff Users" sx={tabSx} />
          <Tab label="Knowledge Base" sx={tabSx} />
          <Tab label="Communications" sx={tabSx} />
          <Tab label="Backups" sx={tabSx} />
        </Tabs>
        {settingsTabIndex === 1 && (
          <Box sx={{ bgcolor: 'action.hover', px: 1 }}>
            <Tabs
              value={caseModuleSubTabIndex}
              onChange={(_, v) => setCaseModuleSubTabIndex(v)}
              sx={{
                minHeight: 40,
                borderBottom: 'none',
                '& .MuiTabs-indicator': { height: 2, backgroundColor: 'secondary.main' },
                '& .MuiTabs-flexContainer': { gap: 0 },
              }}
            >
              <Tab label="Case Templates" sx={subTabSx} />
              <Tab label="Case Field Options" sx={subTabSx} />
              <Tab label="Mailboxes" sx={subTabSx} />
              <Tab label="Workflows" sx={subTabSx} />
            </Tabs>
          </Box>
        )}
      </Box>

      {settingsTabIndex === 1 && (
        <>
          {caseModuleSubTabIndex === 0 && <CaseTemplatesTab onError={setError} />}
          {caseModuleSubTabIndex === 1 && <CaseFieldOptionsTab onError={setError} />}
          {caseModuleSubTabIndex === 2 && <MailboxesTab onError={setError} />}
          {caseModuleSubTabIndex === 3 && <WorkflowsTab onError={setError} />}
        </>
      )}

      
      {settingsTabIndex === 2 && (
        <SupportDeskModuleSettingsTab onError={setError} />
      )}

      {settingsTabIndex === 3 && (
        <TaskModuleSettingsTab onError={setError} />
      )}

      {settingsTabIndex === 4 && (
        <ServiceTypesTab onError={setError} />
      )}

      {settingsTabIndex === 5 && (
        <AssetsModuleTab onError={setError} />
      )}

      {settingsTabIndex === 6 && (
        <OrderingSettingsTab onError={setError} />
      )}

      {settingsTabIndex === 8 && (
        <KnowledgeBaseSettingsTab />
      )}

      {settingsTabIndex === 9 && (
        <>
          <Tabs value={communicationsSubTab} onChange={(_, v) => setCommunicationsSubTab(v)} sx={{ mb: 2, minHeight: 40 }}>
            <Tab value="google-reviews" label="Google Reviews" sx={subTabSx} />
            <Tab value="outage-notifications" label="Outage Notifications" sx={subTabSx} />
          </Tabs>
          {communicationsSubTab === 'google-reviews' && <GoogleReviewSettingsTab />}
          {communicationsSubTab === 'outage-notifications' && <OutageNotificationsTab />}
        </>
      )}

      {settingsTabIndex === 10 && (
        <DbBackupSettingsTab />
      )}

      {settingsTabIndex === 7 && (
        <StaffUsersTab
          users={staffUsers}
          loading={staffUsersLoading}
          error={staffUsersError}
          loaded={staffUsersLoaded}
          onLoad={async () => {
            setStaffUsersLoading(true);
            setStaffUsersError('');
            try {
              const users = await ApiService.adminListUsers();
              setStaffUsers(users);
              setStaffUsersLoaded(true);
            } catch {
              setStaffUsersError('Failed to load users');
            } finally {
              setStaffUsersLoading(false);
            }
          }}
          onUpdate={async (userId: string, field: string, value: boolean) => {
            try {
              const updated = await ApiService.adminUpdateUser(userId, { [field]: value });
              setStaffUsers((prev) => prev.map((u) => u.id === updated.id ? updated : u));
            } catch {
              setStaffUsersError('Failed to update user');
            }
          }}
        />
      )}

      {settingsTabIndex === 0 && (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Master Switch */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Carrier Management
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Master switch to enable or disable all carrier polling.
        </Typography>

        {masterSwitch ? (
          <ListItem>
            <ListItemText
              primary={
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Master Switch
                </Typography>
              }
              secondary={
                <Typography variant="body2" color="text.secondary">
                  {masterSwitch.is_enabled 
                    ? 'All enabled carriers are actively polling' 
                    : 'All carrier polling is disabled'}
                </Typography>
              }
            />
            <ListItemSecondaryAction>
              <Box display="flex" alignItems="center" gap={2}>
                <Chip 
                  label={masterSwitch.is_enabled ? 'ACTIVE' : 'DISABLED'}
                  color={masterSwitch.is_enabled ? 'success' : 'default'}
                  size="small"
                />
                <Switch
                  checked={masterSwitch.is_enabled}
                  onChange={() => handleToggleModule(masterSwitch.module_name, masterSwitch.is_enabled)}
                  disabled={updating === masterSwitch.module_name}
                />
                {updating === masterSwitch.module_name && (
                  <CircularProgress size={20} />
                )}
              </Box>
            </ListItemSecondaryAction>
          </ListItem>
        ) : (
          <Alert severity="warning">
            Master switch not configured. Please contact administrator.
          </Alert>
        )}
      </Paper>

      {/* Individual Carriers */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Individual Carriers
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enable or disable individual carrier polling. Note: These will only poll if the master switch is enabled.
        </Typography>

        {carriers.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" py={4}>
            No carriers configured
          </Typography>
        ) : (
          <List>
            {carriers.map((carrier, index) => {
              const isExpanded = expandedCarriers.has(carrier.id);
              return (
                <React.Fragment key={carrier.id}>
                  <ListItem>
                    <IconButton
                      onClick={() => toggleCarrierExpanded(carrier.id)}
                      size="small"
                      sx={{ mr: 1 }}
                    >
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                            {carrier.name}
                          </Typography>
                          {carrier.is_active && (
                            <Chip label="POLLING" color="success" size="small" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {carrier.description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Type: {carrier.carrier_type} • Interval: {carrier.polling_interval}s
                          </Typography>
                          {carrier.next_poll_time && (
                            <Typography variant="caption" color="primary" display="block">
                              Next check: {formatTime(carrier.next_poll_time)}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Switch
                          checked={carrier.is_enabled}
                          onChange={() => handleToggleCarrier(carrier.id, carrier.is_enabled)}
                          disabled={updating === carrier.id}
                        />
                        {updating === carrier.id && (
                          <CircularProgress size={20} />
                        )}
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ pl: 4, pr: 4, pb: 2 }}>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                          <NotificationsIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1.2rem' }} />
                          Notification Settings
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                          Control which outage events trigger notifications for this carrier
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={carrier.notify_on_created !== false}
                              onChange={() => handleToggleCarrierNotification(carrier.id, 'created', carrier.notify_on_created !== false)}
                              disabled={updating === `${carrier.id}-created`}
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2">Notify on New Outages</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Send notification when a new outage is detected
                              </Typography>
                            </Box>
                          }
                        />
                        {updating === `${carrier.id}-created` && <CircularProgress size={16} sx={{ ml: 1 }} />}
                        <Divider sx={{ my: 1.5 }} />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={carrier.notify_on_updated !== false}
                              onChange={() => handleToggleCarrierNotification(carrier.id, 'updated', carrier.notify_on_updated !== false)}
                              disabled={updating === `${carrier.id}-updated`}
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2">Notify on Outage Updates</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Send notification when an existing outage is updated
                              </Typography>
                            </Box>
                          }
                        />
                        {updating === `${carrier.id}-updated` && <CircularProgress size={16} sx={{ ml: 1 }} />}
                        <Divider sx={{ my: 1.5 }} />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={carrier.notify_on_resolved !== false}
                              onChange={() => handleToggleCarrierNotification(carrier.id, 'resolved', carrier.notify_on_resolved !== false)}
                              disabled={updating === `${carrier.id}-resolved`}
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2">Notify on Outage Resolution</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Send notification when an outage is resolved
                              </Typography>
                            </Box>
                          }
                        />
                        {updating === `${carrier.id}-resolved` && <CircularProgress size={16} sx={{ ml: 1 }} />}
                      </Paper>
                    </Box>
                  </Collapse>
                  {index < carriers.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Paper>

      {/* Notification Services */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Notification Services
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Manage notification providers for sending alerts to staff.
        </Typography>

        {notificationServices.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" py={4}>
            No notification services configured
          </Typography>
        ) : (
          <List>
            {notificationServices.map((service, index) => (
              <React.Fragment key={service.id}>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                          {service.service_name}
                        </Typography>
                        {service.is_connected && (
                          <Chip label="CONNECTED" color="success" size="small" />
                        )}
                        {!service.is_connected && service.last_connection_check && (
                          <Chip label="DISCONNECTED" color="error" size="small" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {service.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Type: {service.service_type}
                        </Typography>
                        {service.last_connection_check && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Last check: {formatTime(service.last_connection_check)}
                          </Typography>
                        )}
                        {service.connection_error && (
                          <Typography variant="caption" color="error" display="block">
                            Error: {service.connection_error}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenTemplateDialog(service)}
                        disabled={updating === service.id}
                        sx={{ mr: 1 }}
                      >
                        Edit Templates
                      </Button>
                      <Tooltip title="Test Connection">
                        <IconButton
                          onClick={() => handleTestConnection(service.id)}
                          disabled={testingConnection === service.id || updating === service.id}
                          color={service.is_connected ? "success" : "default"}
                          size="small"
                        >
                          {testingConnection === service.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <ConnectionIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Send Test Message">
                        <IconButton
                          onClick={() => handleSendTestMessage(service.id)}
                          disabled={sendingTestMessage === service.id || updating === service.id || !service.is_enabled}
                          color="primary"
                          size="small"
                        >
                          {sendingTestMessage === service.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <SendIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Switch
                        checked={service.is_enabled}
                        onChange={() => handleToggleNotificationService(service.id, service.is_enabled)}
                        disabled={updating === service.id}
                      />
                      {updating === service.id && (
                        <CircularProgress size={20} />
                      )}
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < notificationServices.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Template Editing Dialog */}
      <Dialog
        open={templateDialogOpen}
        onClose={handleCloseTemplateDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <EditIcon />
            <Typography variant="h6">
              Edit Notification Templates - {templateDialogService?.service_name}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Tabs value={templateTab} onChange={handleTemplateTabChange} sx={{ mb: 3 }}>
              <Tab label="New Outage" />
              <Tab label="Outage Update" />
              <Tab label="Outage Resolved" />
              <Tab label="Case Reply" />
            </Tabs>
            
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Available Variables:</strong>
                </Typography>
                <Typography variant="caption" component="div">
                  {'{outage_id}, {title}, {description}, {event_type}, {outage_type}, {affected_areas}, {status}, {severity}, {service_type}, {states}, {fault_location}, {outage_url}'}
                </Typography>
                <Typography variant="caption" component="div" sx={{ mt: 1, fontStyle: 'italic' }}>
                  Note: {`{service_type}`} shows branded service name (e.g., "Sonic Mobile (Optus)"), {`{states}`} shows affected states/territories, {`{fault_location}`} is available for mobile outages only. {`{outage_url}`} provides a clickable link to view the outage on the website.
                </Typography>
              </Alert>
            
            <TextField
              fullWidth
              label="Title Template"
              value={templateForm.title}
              onChange={(e) => setTemplateForm(prev => ({ ...prev, title: e.target.value }))}
              margin="normal"
              placeholder="e.g., New Outage: {outage_id}"
              helperText="Template for notification title"
            />
            
            <TextField
              fullWidth
              label="Message Template"
              value={templateForm.message}
              onChange={(e) => setTemplateForm(prev => ({ ...prev, message: e.target.value }))}
              margin="normal"
              multiline
              rows={6}
              placeholder="e.g., 🚨 New Outage Detected\n\nIncident: {outage_id}\n{title}\n{description}\nAreas: {affected_areas}"
              helperText="Template for notification message body"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTemplateDialog}>Cancel</Button>
          <Button
            onClick={handleTestTemplate}
            variant="outlined"
            startIcon={<SendIcon />}
            disabled={testingTemplate || !templateForm.title || !templateForm.message}
          >
            {testingTemplate ? 'Sending...' : 'Test Template'}
          </Button>
          <Button
            onClick={handleSaveTemplate}
            variant="contained"
            disabled={updating === templateDialogService?.id || !templateForm.title || !templateForm.message}
          >
            {updating === templateDialogService?.id ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}
    </Box>
  );
};

export default SettingsPage;

// ---------------------------------------------------------------------------
// StaffUsersTab — inline component for the Staff Users settings tab
// ---------------------------------------------------------------------------

interface StaffUsersTabProps {
  users: any[];
  loading: boolean;
  error: string;
  loaded: boolean;
  onLoad: () => Promise<void>;
  onUpdate: (userId: string, field: string, value: boolean) => Promise<void>;
}

function StaffUsersTab({ users, loading, error, loaded, onLoad, onUpdate }: StaffUsersTabProps) {
  React.useEffect(() => {
    if (!loaded) onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ mt: 2 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Staff Users</Typography>
        <Button size="small" startIcon={<SettingsIcon />} onClick={onLoad} disabled={loading}>
          Refresh
        </Button>
      </Box>
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : (
        <Paper>
          <List>
            {users.map((u, i) => (
              <React.Fragment key={u.id}>
                {i > 0 && <Divider />}
                <ListItem>
                  <ListItemText
                    primary={u.display_name || u.username}
                    secondary={
                      <>
                        {u.username}
                        {u.email ? ` · ${u.email}` : ''}
                        {u.is_admin ? ' · Admin' : ''}
                        {!u.is_active ? ' · Inactive' : ''}
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Can author Knowledge Base articles">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!u.can_author_kb}
                            onChange={(e) => onUpdate(u.id, 'can_author_kb', e.target.checked)}
                            disabled={u.is_admin}
                            size="small"
                          />
                        }
                        label="KB Author"
                        labelPlacement="start"
                        sx={{ mr: 0 }}
                      />
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
            {users.length === 0 && !loading && (
              <ListItem><ListItemText primary="No users found" /></ListItem>
            )}
          </List>
        </Paper>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// KnowledgeBaseSettingsTab — manage categories and tags
// ---------------------------------------------------------------------------

interface KBCategory { id: string; name: string; description: string | null; sort_order: number; }
interface KBTag { id: string; name: string; slug: string; }

function KnowledgeBaseSettingsTab() {
  const [subTab, setSubTab] = React.useState(0);

  // Categories state
  const [categories, setCategories] = React.useState<KBCategory[]>([]);
  const [catLoading, setCatLoading] = React.useState(false);
  const [catError, setCatError] = React.useState('');
  const [catDialog, setCatDialog] = React.useState<{ open: boolean; editing: KBCategory | null }>({ open: false, editing: null });
  const [catName, setCatName] = React.useState('');
  const [catDesc, setCatDesc] = React.useState('');
  const [catOrder, setCatOrder] = React.useState('0');
  const [catSaving, setCatSaving] = React.useState(false);
  const [deleteConfirm, setDeleteConfirm] = React.useState<KBCategory | null>(null);

  // Tags state
  const [tags, setTags] = React.useState<KBTag[]>([]);
  const [tagLoading, setTagLoading] = React.useState(false);
  const [tagError, setTagError] = React.useState('');
  const [newTagName, setNewTagName] = React.useState('');
  const [tagSaving, setTagSaving] = React.useState(false);
  const [tagDeleteConfirm, setTagDeleteConfirm] = React.useState<KBTag | null>(null);

  const loadCategories = React.useCallback(async () => {
    setCatLoading(true);
    setCatError('');
    try {
      setCategories(await ApiService.kbListCategories());
    } catch {
      setCatError('Failed to load categories');
    } finally {
      setCatLoading(false);
    }
  }, []);

  const loadTags = React.useCallback(async () => {
    setTagLoading(true);
    setTagError('');
    try {
      setTags(await ApiService.kbListTags());
    } catch {
      setTagError('Failed to load tags');
    } finally {
      setTagLoading(false);
    }
  }, []);

  React.useEffect(() => { loadCategories(); }, [loadCategories]);
  React.useEffect(() => { if (subTab === 1) loadTags(); }, [subTab, loadTags]);

  const openNew = () => {
    setCatName(''); setCatDesc(''); setCatOrder(String(categories.length));
    setCatDialog({ open: true, editing: null });
  };
  const openEdit = (cat: KBCategory) => {
    setCatName(cat.name); setCatDesc(cat.description || ''); setCatOrder(String(cat.sort_order));
    setCatDialog({ open: true, editing: cat });
  };
  const closeDialog = () => setCatDialog({ open: false, editing: null });

  const handleSaveCategory = async () => {
    if (!catName.trim()) return;
    setCatSaving(true);
    try {
      const data = { name: catName.trim(), description: catDesc.trim() || undefined, sort_order: parseInt(catOrder) || 0 };
      if (catDialog.editing) {
        const updated = await ApiService.kbUpdateCategory(catDialog.editing.id, data);
        setCategories((prev) => prev.map((c) => c.id === updated.id ? updated : c).sort((a, b) => a.sort_order - b.sort_order));
      } else {
        const created = await ApiService.kbCreateCategory(data);
        setCategories((prev) => [...prev, created].sort((a, b) => a.sort_order - b.sort_order));
      }
      closeDialog();
    } catch {
      setCatError('Failed to save category');
    } finally {
      setCatSaving(false);
    }
  };

  const handleDeleteCategory = async (cat: KBCategory) => {
    try {
      await ApiService.kbDeleteCategory(cat.id);
      setCategories((prev) => prev.filter((c) => c.id !== cat.id));
    } catch {
      setCatError('Failed to delete category');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    setTagSaving(true);
    try {
      const tag = await ApiService.kbCreateTag(newTagName.trim());
      setTags((prev) => [...prev, tag].sort((a, b) => a.name.localeCompare(b.name)));
      setNewTagName('');
    } catch {
      setTagError('Failed to create tag');
    } finally {
      setTagSaving(false);
    }
  };

  const handleDeleteTag = async (tag: KBTag) => {
    try {
      await ApiService.kbDeleteTag(tag.id);
      setTags((prev) => prev.filter((t) => t.id !== tag.id));
    } catch {
      setTagError('Failed to delete tag');
    } finally {
      setTagDeleteConfirm(null);
    }
  };

  const subTabSx = { minHeight: 40, textTransform: 'none', fontSize: 13, py: 1 };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>Knowledge Base Settings</Typography>
      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Categories" sx={subTabSx} />
        <Tab label="Tags" sx={subTabSx} />
      </Tabs>

      {/* ---- Categories ---- */}
      {subTab === 0 && (
        <Box>
          {catError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCatError('')}>{catError}</Alert>}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="body2" color="text.secondary">
              Categories group articles in the Knowledge Base. Sort order controls display sequence.
            </Typography>
            <Button variant="contained" size="small" onClick={openNew}>Add Category</Button>
          </Box>
          {catLoading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : (
            <Paper>
              <List disablePadding>
                {categories.map((cat, i) => (
                  <React.Fragment key={cat.id}>
                    {i > 0 && <Divider />}
                    <ListItem>
                      <ListItemText
                        primary={cat.name}
                        secondary={cat.description || <em>No description</em>}
                      />
                      <Box display="flex" alignItems="center" gap={0.5} mr={1}>
                        <Chip label={`Order: ${cat.sort_order}`} size="small" variant="outlined" />
                      </Box>
                      <ListItemSecondaryAction>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(cat)}><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => setDeleteConfirm(cat)}>
                            <SettingsIcon fontSize="small" sx={{ display: 'none' }} />
                            ✕
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </React.Fragment>
                ))}
                {categories.length === 0 && (
                  <ListItem><ListItemText primary="No categories yet. Add one to get started." /></ListItem>
                )}
              </List>
            </Paper>
          )}
        </Box>
      )}

      {/* ---- Tags ---- */}
      {subTab === 1 && (
        <Box>
          {tagError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setTagError('')}>{tagError}</Alert>}
          <Box display="flex" gap={1} mb={2} alignItems="center">
            <TextField
              size="small"
              label="New tag name"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateTag(); } }}
              sx={{ width: 220 }}
            />
            <Button variant="contained" size="small" onClick={handleCreateTag} disabled={tagSaving || !newTagName.trim()}>
              Add Tag
            </Button>
          </Box>
          {tagLoading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : (
            <Box display="flex" gap={1} flexWrap="wrap">
              {tags.map((tag) => (
                <Chip
                  key={tag.id}
                  label={tag.name}
                  onDelete={() => setTagDeleteConfirm(tag)}
                  variant="outlined"
                />
              ))}
              {tags.length === 0 && (
                <Typography variant="body2" color="text.secondary">No tags yet.</Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Category add/edit dialog */}
      <Dialog open={catDialog.open} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{catDialog.editing ? 'Edit Category' : 'New Category'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            required
            value={catName}
            onChange={(e) => setCatName(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
            autoFocus
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={catDesc}
            onChange={(e) => setCatDesc(e.target.value)}
            sx={{ mb: 2 }}
            helperText="Optional — shown in category listings"
          />
          <TextField
            label="Sort order"
            type="number"
            size="small"
            value={catOrder}
            onChange={(e) => setCatOrder(e.target.value)}
            inputProps={{ min: 0 }}
            helperText="Lower numbers appear first"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveCategory} disabled={catSaving || !catName.trim()}>
            {catSaving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category delete confirm */}
      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <Typography>Delete <strong>{deleteConfirm?.name}</strong>? Articles in this category will become uncategorised.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => deleteConfirm && handleDeleteCategory(deleteConfirm)}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Tag delete confirm */}
      <Dialog open={Boolean(tagDeleteConfirm)} onClose={() => setTagDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Tag</DialogTitle>
        <DialogContent>
          <Typography>Delete tag <strong>{tagDeleteConfirm?.name}</strong>? It will be removed from all articles.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTagDeleteConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => tagDeleteConfirm && handleDeleteTag(tagDeleteConfirm)}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
